import { UserPhoto } from "@/src/models/user.model";
import React from "react";
import { DimensionValue, Image, Pressable, View } from "react-native";

type SlotType = "full" | "half" | "third";

type PhotoSlot = Readonly<{
  photo: UserPhoto;
  originalIndex: number;
  type: SlotType;
}>;

type GridRow = readonly PhotoSlot[];

type PhotoGridProps = Readonly<{
  photos: readonly UserPhoto[];
  onPress: (index: number) => void;
}>;

const SLOT_STYLES: Readonly<
  Record<SlotType, { width: DimensionValue; aspectRatio: number }>
> = {
  full: { width: "100%", aspectRatio: 1 / 0.52 },
  half: { width: "49.8%", aspectRatio: 1 / 0.78 },
  third: { width: "33.2%", aspectRatio: 1 },
};

const GRID_PATTERN: readonly [SlotType, number][] = [
  ["full", 1],
  ["half", 2],
  ["third", 3],
];

const buildGridRows = (photos: readonly UserPhoto[]): GridRow[] => {
  const rows: GridRow[] = [];
  let photoIndex = 0,
    patternStep = 0;

  while (photoIndex < photos.length) {
    const [slotType, slotCount] =
      GRID_PATTERN[patternStep++ % GRID_PATTERN.length];
    const row = Array.from({ length: slotCount }, (_, offset) => {
      const photo = photos[photoIndex + offset];
      return photo
        ? { photo, originalIndex: photoIndex + offset, type: slotType }
        : null;
    }).filter(Boolean) as PhotoSlot[];

    rows.push(row);
    photoIndex += slotCount;
  }

  return rows;
};

const PhotoGrid = ({ photos, onPress }: PhotoGridProps) => (
  <View className="gap-0.5 mt-0.5">
    {buildGridRows(photos).map((row, rowIndex) => (
      <View key={rowIndex} className="flex-row gap-0.5">
        {row.map((slot) => {
          const { width, aspectRatio } = SLOT_STYLES[slot.type];
          return (
            <Pressable
              key={slot.photo?.id}
              onPress={() => onPress(slot.originalIndex)}
              style={{ width }}
            >
              {({ pressed }) => (
                <View
                  className={`rounded-sm overflow-hidden w-full ${pressed ? "opacity-80" : "opacity-100"}`}
                  style={{ aspectRatio }}
                >
                  <Image
                    source={{ uri: slot.photo?.url }}
                    className="w-full h-full"
                    resizeMode="cover"
                  />
                </View>
              )}
            </Pressable>
          );
        })}
      </View>
    ))}
  </View>
);

export default PhotoGrid;
